import {
  Button,
  Chip,
  Divider,
  Grid,
  ListItem,
  ListItemIcon,
  makeStyles,
  Typography
} from "@material-ui/core";
import { Launch as LaunchIcon } from "@material-ui/icons";
import { useQuery } from "@redux-requests/react";
import { isEmpty } from "lodash";
import moment from "moment";
import React, { useEffect, useState } from "react";
import ReactHlsPlayer from "react-hls-player";
import { useIntl } from "react-intl";
import ReactPlayer from "react-player";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import videoPlaceholder from "../assets/images/session_placeholder.jpg";
import { Flaticon, Loader } from "../components";
import BaseLayout from "../components/BaseLayout";
import PresentationsList from "../components/PresentationsList";
import config from "../config";
import { types } from "../ducks";
import { createFavourite, getSession } from "../ducks/actions";
import { dateTimeInterval } from "../ducks/utils";
import { RecordingList, SponsoredSession } from "../features/sessions";
import { useActionRequest } from "../hooks";
import useMediaQuery from "../hooks/useMediaQuery";
import useSyncInterval from "../hooks/useSyncInterval";

const PreviewComponent = ({ session }) => {
  const useStyles = makeStyles(theme => ({
    cover: {
      width: "100%",
      height: "520px",
      objectFit: "cover",
      borderRadius: 8,
      backgroundColor: theme.palette.primary.light
    },
    player: {
      background: "black",
      marginLeft: theme.spacing(2),
      marginRight: theme.spacing(2),
      borderRadius: 8
    },
    webinar: {
      border: "none",
      borderRadius: 8
    },
    streamContainer: {
      width: "100%",
      display: "flex",
      justifyContent: "center"
    }
  }));

  const classes = useStyles();

  const [videoUrl, setVideoUrl] = useState();
  const [iframeSrc, setIframeSrc] = useState();

  const isPast = moment(session.programEvent?.endAt).add(30, "minutes").isBefore();
  const isLive =
    !isPast && moment(session.programEvent?.startAt).subtract(30, "minutes").isBefore();

  useEffect(() => {
    if (Array.isArray(session?.recordings) && session?.recordings.length > 0) {
      setVideoUrl(session?.recordings[0].videoUrl);
    } else {
      setVideoUrl(null);
    }
  }, [session]);

  useEffect(() => {
    const authorizedIframeSrc = () => {
      const xhr = new XMLHttpRequest();
      xhr.open(
        "GET",
        `${config.backofficeUrl.replace(
          "{urlName}",
          window.location.hostname.split(".")[0]
        )}/sessions/${session.id}/zoom_webinar`
      );
      xhr.onreadystatechange = () => {
        if (xhr.readyState === XMLHttpRequest.DONE) {
          if (xhr.status === 200) {
            const blob = new Blob([xhr.response], { type: "text/html" });
            setIframeSrc(URL.createObjectURL(blob));
          } else {
            // eslint-disable-next-line no-console
            console.error("Got problem to load zoom webinar iframe :-(");
          }
        }
      };
      xhr.setRequestHeader("Authorization", localStorage.getItem(config.accessTokenName));
      xhr.send();
    };
    authorizedIframeSrc();
  }, [session]);

  if (isLive && session.zoomWebinarJoinUrl && iframeSrc) {
    return (
      <iframe
        className={classes.webinar}
        height="520px"
        width="100%"
        title={session.title}
        src={iframeSrc}
        sandbox="allow-scripts allow-same-origin"
      />
    );
  }

  if (isLive && session.vimeoLiveEventUrl) {
    return (
      <iframe
        className={classes.webinar}
        height="520px"
        width="100%"
        title={session.title}
        src={session.vimeoLiveEventUrl}
        allow="autoplay; fullscreen"
        allowFullScreen
      />
    );
  }

  if (videoUrl) {
    return (
      <>
        <ReactPlayer
          key={videoUrl}
          width="unset"
          height="520px"
          controls
          className={classes.player}
          url={videoUrl}
        />
        <RecordingList
          recordings={session?.recordings}
          onRecordingClick={({ videoUrl }) => setVideoUrl(videoUrl)}
        />
      </>
    );
  }

  if (session.customLiveStreamUrl) {
    return (
      <div className={classes.streamContainer}>
        <ReactHlsPlayer src={session.customLiveStreamUrl} controls width="auto" height="520px" />
      </div>
    );
  }

  if (session.thumbUrl) {
    return <img className={classes.cover} alt={session.title} src={session.thumbUrl} />;
  }

  return <img className={classes.cover} alt={session.title} src={videoPlaceholder} />;
};

const SessionPage = () => {
  const isSmallScreen = useMediaQuery("(max-width: 767px)");
  const useStyles = makeStyles(theme => ({
    root: {
      "& > *": {
        marginBottom: theme.spacing(3)
      }
    },
    toolbar: {
      "& > *": {
        marginRight: theme.spacing(1)
      }
    },
    tagList: {
      "& > *": {
        marginRight: theme.spacing(1)
      }
    },
    headerContainer: {
      display: "flex",
      width: "100%",
      alignItems: "center",
      "& > :first-child": {
        flexGrow: 1
      },
      "& > :last-child": {
        marginLeft: theme.spacing(1)
      }
    },
    headbarItem: {
      marginLeft: isSmallScreen ? 0 : "auto"
    },
    textFormatted: {
      whiteSpace: "pre-line"
    }
  }));
  const classes = useStyles();
  const intl = useIntl();
  const { id } = useParams();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getSession({ id }));
  }, [dispatch, id]);

  const [isPolling, setIsPolling] = useState(false);

  const {
    data: { data: session },
    loading
  } = useQuery({ type: types.GET_SESSION, defaultData: { data: {} } });

  const {
    data: { data: congress }
  } = useQuery({ type: types.GET_CONGRESS, defaultData: { data: {} } });

  const isPast = moment(session.programEvent?.endAt).add(30, "minutes").isBefore();
  const isLive =
    !isPast && moment(session.programEvent?.startAt).subtract(30, "minutes").isBefore();

  useSyncInterval(() => {
    if (!isLive) {
      setIsPolling(true);
      dispatch(getSession({ id }));
    }
  });

  const createFavouriteRequest = useActionRequest({
    action: createFavourite
  });

  const onFavouriteCreate = () => {
    createFavouriteRequest({ favouritableId: id, favouritableType: "Session" });
  };

  return (
    <BaseLayout className={classes.root}>
      <Loader loading={loading && !isPolling}>
        <SponsoredSession session={session} />

        <PreviewComponent session={session} />
        <Grid container>
          <Grid item xs={12} lg={6}>
            <Typography variant="h4">
              <strong>{session.title} </strong>
            </Typography>
          </Grid>
          <Grid container item xs={12} lg={6} spacing={2}>
            <Grid item xs={12} lg={6}>
              {congress.enableOpenInApp && session.zoomWebinarJoinUrl && !isPast && (
                <Button
                  fullWidth
                  size="large"
                  variant="outlined"
                  startIcon={<LaunchIcon />}
                  color="primary"
                  href={session.zoomWebinarJoinUrl}
                  target="_blank"
                  className={classes.headbarItem}
                >
                  {intl.formatMessage({
                    id: "Pages.SessionPage.OpenZoomApp",
                    defaultMessage: "Open Zoom App for Interpretation"
                  })}
                </Button>
              )}
            </Grid>
            <Grid item xs={12} lg={6}>
              <Button
                fullWidth
                size="large"
                variant="contained"
                startIcon={<Flaticon icon="star" />}
                color="primary"
                disabled={session.isFavourite}
                onClick={onFavouriteCreate}
                className={classes.headbarItem}
              >
                {intl.formatMessage({
                  id: "Pages.SessionPage.Favourite",
                  defaultMessage: "Favourite"
                })}
              </Button>
            </Grid>
          </Grid>
        </Grid>
        <Typography variant="body1">
          {intl.formatMessage(
            { id: "Pages.SessionPage.Room", defaultMessage: "Room {rooms}" },
            { rooms: session.programEvent?.roomList.join(", ") }
          )}
          &nbsp;&#8226;&nbsp;
          {dateTimeInterval(session.programEvent?.startAt, session.programEvent?.endAt)}
        </Typography>
        <Divider />

        {session?.sessionPresentations && (
          <PresentationsList presentations={session?.sessionPresentations} />
        )}

        <Grid container spacing={3}>
          {!isEmpty(session.moderators) && (
            <Grid item component={ListItem} xs={12} sm={6}>
              <ListItemIcon>
                <Flaticon icon="user" fontSize="1.5rem" />
              </ListItemIcon>
              <div>
                <Typography variant="h6">
                  <strong>
                    {intl.formatMessage({
                      id: "Pages.SessionPage.Moderators",
                      defaultMessage: "Moderators"
                    })}
                  </strong>
                </Typography>
                <Typography variant="body2">
                  {session.moderators
                    ?.map(({ firstName, lastName }) => `${firstName} ${lastName}`)
                    .join(", ")}
                </Typography>
              </div>
            </Grid>
          )}
          {!isEmpty(session.speakers) && (
            <Grid item component={ListItem} xs={12} sm={6}>
              <ListItemIcon>
                <Flaticon icon="seminar" fontSize="1.5rem" />
              </ListItemIcon>
              <div>
                <Typography variant="h6">
                  <strong>
                    {intl.formatMessage({
                      id: "Pages.SessionPage.Speakers",
                      defaultMessage: "Speakers"
                    })}
                  </strong>
                </Typography>
                <Typography variant="body2">
                  {session.speakers
                    ?.map(({ firstName, lastName }) => `${firstName} ${lastName}`)
                    .join(", ")}
                </Typography>
              </div>
            </Grid>
          )}
        </Grid>
        <Divider />
        <Typography variant="body2" className={classes.textFormatted}>
          {session.description}
        </Typography>
        <div className={classes.tagList}>
          {session.tagList?.map(tag => (
            <Chip key={tag} label={tag} variant="outlined" />
          ))}
        </div>
      </Loader>
    </BaseLayout>
  );
};

export default SessionPage;
